/**
 * The A4 renderer.
 *
 * A pure function from a `NormalizedDocument` plus the issuing business to a
 * complete HTML document. No React, no DOM, no database — it returns a string.
 * That is what lets the browser print path and the PDF path share ONE code
 * path: both take this exact string and hand it to the same engine.
 *
 * IT CONTAINS NO ARITHMETIC. Every figure is read from the model, which was
 * already reconciled and validated by `normalizeDocument()`. A renderer that
 * summed anything for itself is how a printed invoice ends up disagreeing
 * with its own tax summary.
 *
 * THE GEOMETRY IS NOT NEGOTIABLE HERE. Column widths come from `geometry.ts`
 * and are emitted as millimetre `<col>` elements. The stylesheet's
 * `table-layout: fixed` then makes the content powerless to move them.
 */
import { formatAmount, formatQuantity, formatRateBp } from '../money/format';

/**
 * Wraps an amount in parentheses for credit notes (presentation-only).
 * Credit notes show negative values as parenthesized amounts.
 */
function creditAmount(m: NormalizedDocument, amount: string): string {
  return m.docType === 'CREDIT_NOTE' ? `(${amount})` : amount;
}
import { asMoney } from '../money/types';
import type { Money } from '../money/types';
import { stateNameFor } from '../gst/stateCodes';
import { a4Stylesheet } from './a4Stylesheet';
import { modeClass } from './renderMode';
import {
  LETTERHEAD_COLUMNS_MM,
  TOTALS_COLUMNS_MM,
  colgroupMm,
  hsnSummaryColumns,
  itemColumnOrder,
  itemColumns,
  taxSummaryColumns,
} from './geometry';
import type { ItemColumnKey, ItemColumnOptions, TaxLegKey } from './geometry';
import type { NormalizedDocument, NormalizedLine } from './types';

/**
 * What the business needs to print its own letterhead.
 *
 * This is a PRINT projection, not the `Business` row: it is assembled by
 * `toBusinessIdentity` and it carries only what belongs on paper. Adding a
 * field here is how a letterhead gains detail; nothing in `core/` knows that a
 * business master exists.
 */
export interface BusinessIdentity {
  readonly legalName: string;
  /** Printed beneath the legal name, and only when it differs from it. */
  readonly tradeName?: string;
  /**
   * The logo, INLINED as a `data:` URL, or absent.
   *
   * A data URL and not a blob: `renderA4` is pure and synchronous, and the
   * printed document is a standalone page in an iframe where a `blob:` URL from
   * the parent document resolves to nothing. See `attachmentService`.
   */
  readonly logoDataUrl?: string;
  /** The signature image, inlined the same way. */
  readonly signatureDataUrl?: string;
  readonly gstin?: string;
  readonly addressLines: readonly string[];
  readonly phone?: string;
  readonly email?: string;
  readonly website?: string;
  readonly stateName?: string;
  readonly bankName?: string;
  readonly accountNumber?: string;
  readonly ifsc?: string;
  readonly signatory?: string;
}

/** The letterhead's column set lives in `geometry.ts`, checked at module load. */

export interface A4RenderOptions {
  /** Shown beside the document number on a quotation; absent on an invoice. */
  readonly referenceNumber?: string;
  /**
   * The invoice a Credit Note or Sales Return corrects.
   *
   * Passed in by the caller rather than looked up, for the same reason
   * `referenceNumber` is: `core/` must not reach into the database to decide what
   * to print, and the document model carries only the SOURCE ID, not its number.
   */
  readonly correctsReferenceNumber?: string;
  readonly creditReason?: string;
}

const TITLE_BY_DOC_TYPE: Record<string, string> = {
  PROFORMA: 'PROFORMA INVOICE',
  QUOTATION: 'QUOTATION',
  SALES_ORDER: 'SALES ORDER',
  DELIVERY_NOTE: 'DELIVERY NOTE',
  SALES_INVOICE: 'TAX INVOICE',
  CREDIT_NOTE: 'CREDIT NOTE',
  SALES_RETURN: 'SALES RETURN',
  PURCHASE_ORDER: 'PURCHASE ORDER',
  PURCHASE_RECEIPT: 'PURCHASE RECEIPT',
  PURCHASE_INVOICE: 'PURCHASE INVOICE',
  DEBIT_NOTE: 'DEBIT NOTE',
  PURCHASE_RETURN: 'PURCHASE RETURN',
};

/** Escape text for HTML. Addresses and item names are user-supplied. */
export function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * The tax shown on a line: CGST+SGST, or IGST, or cess. Never more than the
 * split the document actually carries - `normalizeDocument` has already
 * refused a document that claims both, so summing all four legs here totals
 * the same figure whichever split applied.
 */
function lineTaxPaise(l: NormalizedLine): Money {
  return asMoney(l.cgstPaise + l.sgstPaise + l.igstPaise + l.cessPaise);
}

/**
 * Which tax legs THIS document prints.
 *
 * Read off `taxSplit`, which `normalizeDocument` derived from the stored
 * amounts and validated. Deciding it here from the amounts instead would be a
 * second, unvalidated opinion about a tax document.
 */
function taxLegsOf(m: NormalizedDocument): readonly TaxLegKey[] {
  switch (m.taxSplit) {
    case 'CGST_SGST':
      return m.totals.cessPaise !== 0 ? ['cgst', 'sgst', 'cess'] : ['cgst', 'sgst'];
    case 'IGST':
      return m.totals.cessPaise !== 0 ? ['igst', 'cess'] : ['igst'];
    case 'CESS_ONLY':
      return ['cess'];
    case 'NONE':
      return [];
  }
}

const LEG_HEADING: Record<TaxLegKey, string> = {
  cgst: 'CGST',
  sgst: 'SGST / UTGST',
  igst: 'IGST',
  cess: 'Cess',
};

/** The cell class for an item column, by key. The order IS the contract. */
function itemCellClass(key: ItemColumnKey): string {
  if (key === 'index' || key === 'unit' || key === 'hsn') return 'doc-ctr';
  if (key === 'description') return 'doc-wrap';
  return 'doc-num';
}

/** One label/value pair in the totals block. */
function totalRow(label: string, value: string, cls = ''): string {
  return (
    `<tr${cls ? ` class="${cls}"` : ''}>` +
    `<td class="doc-total-label">${esc(label)}</td>` +
    `<td class="doc-total-value">${value}</td></tr>`
  );
}

/** Which optional item columns this document needs. */
function itemColumnOptions(m: NormalizedDocument): ItemColumnOptions {
  const isDeliveryNote = m.docType === 'DELIVERY_NOTE';

  // Delivery Note: goods-focused, no financial columns
  if (isDeliveryNote) {
    return {
      showHsn: m.lines.some((l) => l.hsnSac !== undefined && l.hsnSac !== ''),
      showDiscount: false,
      showRate: false,
      showTaxable: false,
      showTax: false,
      showTotal: false,
      showUnit: true,
      showQuantity: true,
    };
  }

  return {
    // A column of empty cells is worse than no column: it reads as "we do not
    // know the HSN", which is a different statement from "there is none here".
    showHsn: m.lines.some((l) => l.hsnSac !== undefined && l.hsnSac !== ''),
    showDiscount: m.lines.some((l) => l.discountPaise !== 0),
  };
}

function renderItemRow(
  m: NormalizedDocument,
  l: NormalizedLine,
  showFreeQty: boolean,
  order: readonly ItemColumnKey[],
): string {
  const qty = showFreeQty
    ? `${formatQuantity(l.quantityMilli)} + ${formatQuantity(l.freeQuantityMilli)} F`
    : formatQuantity(l.quantityMilli);

  // `descriptionExtra` was normalised and stored but never printed. It is where
  // a second descriptive line lives, and dropping it silently discarded data
  // the user had entered deliberately.
  // Show product name (if available) as the primary label, and user-entered
  // description as a subtitle. This mirrors the editor UI where product name
  // and description are shown separately.
  const description = l.productName
    ? `<div class="doc-line-product">${esc(l.productName)}</div>` +
      (l.description ? `<div class="doc-line-description">${esc(l.description)}</div>` : '') +
      (l.descriptionExtra ? `<div class="doc-line-extra">${esc(l.descriptionExtra)}</div>` : '')
    : `<div>${esc(l.description)}</div>` +
      (l.descriptionExtra ? `<div class="doc-line-extra">${esc(l.descriptionExtra)}</div>` : '');

  const cells: Record<ItemColumnKey, string> = {
    index: esc(String(l.lineNo)),
    description,
    hsn: l.hsnSac ? esc(l.hsnSac) : '',
    quantity: esc(qty),
    qtyOrdered: '',
    qtyDelivered: '',
    qtyBalance: '',
    unit: esc(l.unit),
    rate: formatAmount(l.ratePaise),
    discount: l.discountPaise !== 0 ? formatAmount(asMoney(-l.discountPaise)) : '',
    taxable: creditAmount(m, formatAmount(l.taxablePaise)),
    // The rate alongside the amount, so a reader never has to cross-reference
    // the summary table to know what was charged on this row.
    tax: `${esc(formatRateBp(l.taxRateBp))}%<div class="doc-line-tax">${creditAmount(m, formatAmount(lineTaxPaise(l)))}</div>`,
    total: creditAmount(m, formatAmount(l.totalPaise)),
  };
  return (
    '<tr>' +
    order.map((key) => `<td class="${itemCellClass(key)}">${cells[key] ?? ''}</td>`).join('') +
    '</tr>'
  );
}

/**
 * How this document names ITS OWN number in the meta table.
 *
 * A single shared renderer serving every document type cannot print one hard-coded
 * label. "Invoice No." on a Delivery Note is not a cosmetic slip: it tells the
 * person holding the paper that this is a tax invoice, which is the exact document
 * a delivery note is NOT, and the reason the stock and the billing stay separate.
 */
const NUMBER_LABEL_BY_DOC_TYPE: Record<string, string> = {
  QUOTATION: 'Quotation No.',
  PROFORMA: 'Proforma No.',
  SALES_ORDER: 'Order No.',
  DELIVERY_NOTE: 'Delivery Note No.',
  SALES_INVOICE: 'Invoice No.',
  CREDIT_NOTE: 'Credit Note No.',
  SALES_RETURN: 'Return No.',
  PURCHASE_ORDER: 'Purchase Order No.',
  PURCHASE_RECEIPT: 'Receipt No.',
  PURCHASE_INVOICE: 'Purchase Invoice No.',
  DEBIT_NOTE: 'Debit Note No.',
  PURCHASE_RETURN: 'Purchase Return No.',
};

/**
 * Who the goods go to, in the buyer's corner.
 *
 * "Bill To" is right for a tax document and wrong for a dispatch: a delivery note
 * is signed for at the door, so it names the DELIVERY address. Where no separate
 * delivery address was captured, the billing address is what the note describes,
 * and the caption says so rather than inventing a shipment that did not happen.
 */
function partyCaption(m: NormalizedDocument): string {
  // Purchase Order: the supplier is the counterparty being addressed,
  // so the right-hand party is captioned "To" rather than "From".
  if (m.docType === 'PURCHASE_ORDER') return 'To';
  if (m.docType === 'QUOTATION') return 'To';
  // A dispatch is signed for at the door, so it names the DELIVERY corner. When no
  // separate delivery address was captured, the billing address is still what the
  // note describes — so the caption is right either way.
  if (m.docType === 'DELIVERY_NOTE' || m.docType === 'PURCHASE_RECEIPT') return 'Deliver To';
  if (
    m.docType === 'PURCHASE_INVOICE' ||
    m.docType === 'PURCHASE_RECEIPT' ||
    m.docType === 'DEBIT_NOTE' ||
    m.docType === 'PURCHASE_RETURN'
  )
    return 'From';
  return 'Bill To';
}

/** Seller, buyer, and the document's own facts. */
function renderHeader(m: NormalizedDocument, biz: BusinessIdentity, opt: A4RenderOptions): string {
  const title = TITLE_BY_DOC_TYPE[m.docType] ?? m.docType;

  // The seller. `tradeName` is omitted when it merely repeats the legal name,
  // because printing "ACME TRADING" under "ACME TRADING PVT LTD" is noise.
  const contact = (phone?: string, email?: string): string => {
    const parts = [phone ? `Ph: ${phone}` : '', email ?? ''].filter(Boolean);
    return parts.length ? `<div class="doc-biz-line">${esc(parts.join('  ·  '))}</div>` : '';
  };
  const sellerCaption =
    m.docType === 'DELIVERY_NOTE'
      ? '<div class="doc-caption">Dispatched From</div>'
      : m.docType === 'PURCHASE_RECEIPT'
        ? '<div class="doc-caption">Received From</div>'
        : m.docType === 'PURCHASE_ORDER'
          ? '<div class="doc-caption">From</div>'
          : '';
  const seller = [
    sellerCaption,
    `<div class="doc-biz-name">${esc(biz.legalName)}</div>`,
    biz.tradeName && biz.tradeName !== biz.legalName
      ? `<div class="doc-biz-line">${esc(biz.tradeName)}</div>`
      : '',
    ...biz.addressLines.map((a) => `<div class="doc-biz-line">${esc(a)}</div>`),
    biz.gstin ? `<div class="doc-gstin">GSTIN: ${esc(biz.gstin)}</div>` : '',
    contact(biz.phone, biz.email),
    biz.website ? `<div class="doc-biz-line">${esc(biz.website)}</div>` : '',
  ]
    .filter(Boolean)
    .join('');

  // The logo sits ABOVE the legal name, in the seller's column.
  //
  // Above and not beside, because the seller column is sized in millimetres by
  // the letterhead colgroup and a logo beside the name would force that column
  // to grow on every page. It also reads as a letterhead: mark, then who it is.
  //
  // The src is a data URL resolved by `attachmentService`; `esc` is applied
  // because it is still attacker-influenced text as far as this function knows -
  // a stored row can hold anything, and an unescaped quote would break out of
  // the attribute.
  const logo = biz.logoDataUrl
    ? `<img class="doc-logo" src="${esc(biz.logoDataUrl)}" alt="${esc(biz.legalName)}">`
    : '';

  const p = m.party;
  const buyer = [
    p.gstin ? `<div class="doc-gstin">GSTIN: ${esc(p.gstin)}</div>` : '',
    ...p.addressLines.map((a) => `<div class="doc-biz-line">${esc(a)}</div>`),
    contact(p.phone, p.email),
  ]
    .filter(Boolean)
    .join('');

  // A separate SHIP TO block, only when this sale was delivered somewhere
  // other than the billing address. Three blocks will not sit side by side on
  // 190mm, so delivery gets its own captioned row underneath instead.
  const shipTo =
    m.deliveryAddressLines && m.deliveryAddressLines.length > 0
      ? `<tr><td class="doc-party" colspan="2">
           <div class="doc-caption">Ship To</div>
           ${m.deliveryAddressLines.map((a) => `<div class="doc-biz-line">${esc(a)}</div>`).join('')}
           ${m.deliveryStateName ? `<div class="doc-biz-line">${esc(m.deliveryStateName)}</div>` : ''}
         </td></tr>`
      : '';

  const meta: Array<readonly [string, string] | null> = [
    [NUMBER_LABEL_BY_DOC_TYPE[m.docType] ?? 'Invoice No.', esc(m.docNumber)],
    m.docType === 'PURCHASE_RECEIPT' ? ['Receipt Date', esc(m.docDate)] : ['Date', esc(m.docDate)],
    m.expiryDate && (m.docType === 'QUOTATION' || m.docType === 'PROFORMA')
      ? ['Valid Until', esc(m.expiryDate)]
      : null,
    m.dueDate && m.docType === 'PURCHASE_INVOICE'
      ? ['Payment Due By', esc(m.dueDate)]
      : m.dueDate
        ? ['Due Date', esc(m.dueDate)]
        : null,
    opt.referenceNumber ? ['Your Ref', esc(opt.referenceNumber)] : null,
    m.docType === 'SALES_ORDER' && opt.referenceNumber
      ? ['Customer PO', esc(opt.referenceNumber)]
      : null,
    m.docType === 'SALES_ORDER' && m.expectedDate
      ? ['Expected Delivery', esc(m.expectedDate)]
      : null,
    m.docType === 'PURCHASE_ORDER' && opt.referenceNumber
      ? ["Buyer's Ref", esc(opt.referenceNumber)]
      : null,
    m.docType === 'PURCHASE_ORDER' && m.expectedDate ? ['Delivery By', esc(m.expectedDate)] : null,
    m.docType === 'PURCHASE_RECEIPT' && opt.referenceNumber
      ? ['Your Ref', esc(opt.referenceNumber)]
      : null,
    // The document this one corrects, for a Credit Note or Sales Return.
    //
    // Without it, a customer holding both papers cannot match them, and the shop
    // cannot answer "which invoice was this against?" from the return alone.
    // Printed only when a source exists, so every other document is unaffected.
    opt.correctsReferenceNumber
      ? [
          m.docType === 'DEBIT_NOTE' || m.docType === 'PURCHASE_RETURN'
            ? 'Against Bill'
            : 'Against Invoice',
          esc(opt.correctsReferenceNumber),
        ]
      : null,
    opt.creditReason &&
    (m.docType === 'CREDIT_NOTE' ||
      m.docType === 'DEBIT_NOTE' ||
      m.docType === 'SALES_RETURN' ||
      m.docType === 'PURCHASE_RETURN')
      ? ['Reason', esc(opt.creditReason)]
      : null,
    m.expectedDate ? ['Expected', esc(m.expectedDate)] : null,
    // The PLACE OF SUPPLY the calculation used - not the buyer's registered
    // state. The two differ whenever goods were delivered elsewhere, and
    // printing the buyer's state here is how an invoice ends up contradicting
    // its own tax split.
    ['Place of Supply', `${esc(stateNameFor(m.placeOfSupplyCode))} (${esc(m.placeOfSupplyCode)})`],
  ];
  const metaRows = meta
    .filter((r): r is readonly [string, string] => r !== null)
    .map(
      ([k, v]) =>
        `<tr><td class="doc-label-cell">${esc(k)}</td>` +
        `<td class="doc-value-cell">${v}</td></tr>`,
    )
    .join('');

  // A DRAFT that prints like an issued document is a real hazard: it can be sent
  // to a customer and filed as a tax invoice. `status` was already in the model
  // and never rendered, so the two were indistinguishable on paper.
  //
  // The wording is chosen by what the document actually claims. A tax document
  // warns that it is not yet a valid invoice. A DELIVERY NOTE claims no tax at
  // all, so telling the driver it is "not a valid tax invoice" invites exactly
  // the wrong question — what it must say is that the goods have NOT left, because
  // stock only moves when this is issued, and a dispatch slip signed for undelivered
  // goods is a receipt for a lie.
  //
  // A PURCHASE ORDER is the same kind of document and was previously lumped in with
  // the tax documents, which made a draft order claim it was an incomplete tax
  // invoice. It is not one at all: `PURCHASE_ORDER.taxDocument` is false, and no
  // amount on it is a tax liability until a supplier invoice arrives. What a draft
  // order has NOT yet done is commit the shop, and the wording names the counterparty
  // — a draft order is not the same paper to a supplier as it is to a customer, and
  // "not yet sent" would be nonsense on a sales order.
  const DRAFT_NOT_SENT: Partial<Record<string, string>> = {
    PURCHASE_ORDER: 'Draft — not yet sent to the supplier',
    SALES_ORDER: 'Draft — not yet sent to the customer',
  };
  const draftBanner =
    m.status === 'DRAFT'
      ? `<div class="doc-draft-banner">${
          m.docType === 'DELIVERY_NOTE' || m.docType === 'PURCHASE_RECEIPT'
            ? 'Draft — goods not dispatched'
            : (DRAFT_NOT_SENT[m.docType] ?? 'Draft — not a valid tax invoice')
        }</div>`
      : '';

  return `
<div class="doc-head">
  <div class="doc-title">${esc(title)}</div>
  ${m.docType === 'PROFORMA' ? '<div class="doc-proforma-banner"><div class="doc-proforma-banner-title">PROFORMA -- NOT A TAX INVOICE</div><div class="doc-proforma-banner-text">A preliminary estimate, not a GST-creating document. A formal Tax Invoice will follow upon finalisation.</div></div>' : ''}
  ${draftBanner}
  ${opt.correctsReferenceNumber && m.docType === 'CREDIT_NOTE' ? '<div class="doc-correction-notice">This credit note corrects Invoice ' + esc(opt.correctsReferenceNumber) + '</div>' : ''}
  ${m.docType === 'CREDIT_NOTE' ? '<div class="doc-credit-note">Amounts shown in parenthesis are credits.</div>' : ''}
  ${opt.correctsReferenceNumber && m.docType === 'DEBIT_NOTE' ? '<div class="doc-correction-notice">This debit note corrects Bill ' + esc(opt.correctsReferenceNumber) + '</div>' : ''}
  ${opt.correctsReferenceNumber && m.docType === 'PURCHASE_RETURN' ? '<div class="doc-correction-notice">This purchase return corrects Bill ' + esc(opt.correctsReferenceNumber) + '</div>' : ''}
  ${m.docType === 'PURCHASE_ORDER' && m.notes ? `<div class="doc-delivery-instructions"><span class="doc-label">Delivery Instructions:</span> ${esc(m.notes)}</div>` : ''}
  <table class="doc-letterhead">
    <colgroup>${colgroupMm(LETTERHEAD_COLUMNS_MM, ['seller', 'buyer'])}</colgroup>
    <tbody>
      <tr>
        <td class="doc-party">${logo}${seller}</td>
        <td class="doc-party">
          <div class="doc-caption">${partyCaption(m)}</div>
          <div class="doc-strong">${esc(p.name)}</div>
          ${buyer}
          <table class="doc-meta">
            <tbody>${metaRows}</tbody>
          </table>
        </td>
      </tr>
      ${shipTo}
    </tbody>
  </table>
</div>`;
}

/**
 * The item table.
 *
 * Columns are chosen by what this document actually contains: an invoice whose
 * lines carry no HSN prints no HSN column, and one with no line discount
 * prints no discount column. `itemColumns()` hands every absent column's width
 * back to the description, so the table still ends on the same right edge.
 */
function renderItems(m: NormalizedDocument): string {
  const opt = itemColumnOptions(m);
  const order = itemColumnOrder(opt);
  const showFreeQty = m.lines.some((l) => l.freeQuantityMilli > 0);

  const heading: Record<string, string> = {
    index: '#',
    description: 'Description',
    hsn: 'HSN/SAC',
    quantity:
      m.docType === 'DELIVERY_NOTE'
        ? 'Qty Del.'
        : m.docType === 'SALES_ORDER' || m.docType === 'PURCHASE_ORDER'
          ? 'Qty Ord.'
          : m.docType === 'SALES_RETURN' || m.docType === 'PURCHASE_RETURN'
            ? 'Qty Ret.'
            : 'Qty',
    unit: 'Unit',
    rate: 'Rate',
    discount: 'Disc.',
    taxable: 'Taxable',
    tax: 'GST',
    total: 'Amount',
  };
  // "Amount" rather than "Total" for the line: the block beneath it is also
  // called "Total", and two different totals one under the other reads as an
  // error even when both are correct.
  const head = order.map((k) => `<th>${esc(heading[k] ?? k)}</th>`).join('');

  return `
<table class="doc-table">
  <colgroup>${colgroupMm(itemColumns(opt), order)}</colgroup>
  <thead>
    <tr>${head}</tr>
  </thead>
  <tbody>${m.lines.map((l) => renderItemRow(m, l, showFreeQty, order)).join('')}</tbody>
</table>`;
}

/**
 * The totals block, and the only place in the document a reader looks for "how
 * much do I owe".
 *
 * Hierarchy, in order: real subtotal figures, then the tax legs individually,
 * then the grand total as the only emphasised row. Amount in words sits
 * directly above the grand total because it is the same number said twice.
 *
 * CHARGES ARE NOT DOUBLE-COUNTED. The old block printed a "Taxable Charges"
 * aggregate AND every charge line beneath it - and `taxableChargesPaise` is the
 * SUM of the taxable charges, not a fourth charge. A customer adding up the
 * column got the wrong answer, and the grand total then appeared not to match
 * its own rows. Only the individual, labelled charges are printed now; the
 * aggregates remain in the model because tax is computed on them.
 */
function renderTotals(m: NormalizedDocument): string {
  const t = m.totals;
  // Delivery Note: goods-movement document. Show only Total Amount
  // (no tax breakdown) and Total Qty Delivered.
  if (m.docType === 'DELIVERY_NOTE') {
    return `
<div class="doc-totals">
  <table>
    <colgroup>${colgroupMm(TOTALS_COLUMNS_MM, ['label', 'value'])}</colgroup>
    <tbody>
      ${totalRow('Total Amount', formatAmount(t.grandTotalPaise), 'doc-grand')}
    </tbody>
  </table>
</div>`;
  }
  const legs = taxLegsOf(m);
  const legAmount: Record<TaxLegKey, Money> = {
    cgst: t.cgstPaise,
    sgst: t.sgstPaise,
    igst: t.igstPaise,
    cess: t.cessPaise,
  };

  const rows = [
    totalRow('Subtotal', creditAmount(m, formatAmount(t.grossSubtotalPaise))),
    // A discount of zero is not a fact worth a line; the column exists to show
    // that money came off, not to prove that none did.
    t.totalDiscountPaise !== 0
      ? totalRow('Discount', formatAmount(asMoney(-t.totalDiscountPaise)), 'doc-sub')
      : '',
    totalRow('Taxable Value', creditAmount(m, formatAmount(t.taxablePaise))),
    // Each charge by its own label, exactly once.
    ...m.charges.map((c) => totalRow(c.label, formatAmount(c.amountPaise))),
    // Each tax leg on its own line. A single "Total Tax" row was arithmetically
    // correct and practically useless: CGST and SGST are separate liabilities
    // and a customer reconciling them needs both figures.
    ...legs.map((leg) => totalRow(LEG_HEADING[leg], creditAmount(m, formatAmount(legAmount[leg])))),
    t.tcsPaise !== 0 ? totalRow('TCS @ 1%', formatAmount(t.tcsPaise)) : '',
    // Round off is only worth printing when there is something to round off.
    // A "-0.50" line on an invoice that never rounded off reads as an error.
    t.roundOffPaise !== 0
      ? totalRow(
          t.roundOffPaise > 0 ? 'Round Off (+)' : 'Round Off (−)',
          formatAmount(t.roundOffPaise),
          'doc-sub',
        )
      : '',
    totalRow(
      m.docType === 'PROFORMA'
        ? 'ESTIMATED TOTAL'
        : m.docType === 'CREDIT_NOTE'
          ? 'TOTAL CREDIT'
          : m.docType === 'DEBIT_NOTE'
            ? 'ADDITIONAL AMOUNT DUE'
            : m.docType === 'SALES_RETURN'
              ? 'TOTAL RETURN VALUE'
              : m.docType === 'PURCHASE_RETURN'
                ? 'CREDIT NOTE AMOUNT'
                : m.docType === 'SALES_ORDER' || m.docType === 'PURCHASE_ORDER'
                  ? 'ORDER TOTAL'
                  : m.docType === 'PURCHASE_RECEIPT'
                    ? 'Total Received Value'
                    : 'Grand Total',
      creditAmount(m, formatAmount(t.grandTotalPaise)),
      'doc-grand',
    ),
  ].filter(Boolean);

  return `
<div class="doc-words">
  <span class="doc-label">Amount in words:</span> ${esc(m.amountInWords)}
</div>
<table class="doc-totals">
  <colgroup>${colgroupMm(TOTALS_COLUMNS_MM, ['label', 'value'])}</colgroup>
  <tbody>${rows.join('')}</tbody>
</table>`;
}

/**
 * The rate-wise GST breakup. A statutory part of a tax invoice: one row per
 * distinct rate, never one row for the whole document.
 *
 * The column set comes from `taxSummaryColumns(legs)`, so an intra-state
 * invoice gets CGST+SGST and an inter-state one gets IGST, and both still end
 * on the item table's right edge. The two hand-written width maps this replaces
 * were the only places in the renderer where a number was asserted to sum to
 * 190 without anything checking it.
 */
function renderTaxSummary(m: NormalizedDocument): string {
  if (
    m.docType === 'DELIVERY_NOTE' ||
    m.docType === 'SALES_RETURN' ||
    m.docType === 'SALES_ORDER' ||
    m.docType === 'PURCHASE_ORDER'
  )
    return '';
  if (m.taxSummary.length === 0) return '';
  const legs = taxLegsOf(m);
  // A document can legitimately carry NO tax legs - nil-rated, exempt, or below
  // the threshold. There is no rate-wise summary to print in that case, and
  // asking `taxSummaryColumns` for the widths of zero legs used to throw
  // `RangeError: Cannot split 116mm into 0 parts`, which took down the whole
  // document screen. The table is OMITTED, not faked.
  const widths = taxSummaryColumns(legs);
  if (!widths) return '';

  const cols = ['rate', 'taxable', ...legs];
  const head =
    `<th>Rate</th><th class="doc-num">Taxable Value</th>` +
    legs.map((leg) => `<th class="doc-num">${esc(LEG_HEADING[leg])}</th>`).join('');

  const rows = m.taxSummary
    .map((r) => {
      const legValue: Record<TaxLegKey, Money> = {
        cgst: r.cgstPaise,
        sgst: r.sgstPaise,
        igst: r.igstPaise,
        cess: r.cessPaise,
      };
      const tds = [
        `<td class="doc-ctr">${esc(formatRateBp(r.taxRateBp))}%</td>`,
        `<td class="doc-num">${formatAmount(r.taxablePaise)}</td>`,
        ...legs.map((leg) => `<td class="doc-num">${formatAmount(legValue[leg])}</td>`),
      ].join('');
      return `<tr>${tds}</tr>`;
    })
    .join('');

  return `
<div class="doc-summary-block">
  <div class="doc-summary-title">GST Summary</div>
  <table class="doc-summary">
    <colgroup>${colgroupMm(widths, cols)}</colgroup>
    <thead><tr>${head}</tr></thead>
    <tbody>${rows}</tbody>
  </table>
</div>`;
}

/** The HSN-wise breakup, one row per distinct code. */
function renderHsnSummary(m: NormalizedDocument): string {
  if (
    m.docType === 'DELIVERY_NOTE' ||
    m.docType === 'SALES_RETURN' ||
    m.docType === 'SALES_ORDER' ||
    m.docType === 'PURCHASE_ORDER'
  )
    return '';
  if (m.hsnSummary.length === 0) return '';
  const legs = taxLegsOf(m);
  const widths = hsnSummaryColumns(legs);
  // Same guard as `renderTaxSummary`: no legs, no tax columns, no table.
  if (!widths) return '';

  const cols = ['hsn', 'taxable', ...legs];
  const head =
    `<th>HSN/SAC</th><th class="doc-num">Taxable</th>` +
    legs.map((leg) => `<th class="doc-num">${esc(LEG_HEADING[leg])}</th>`).join('');

  const rows = m.hsnSummary
    .map((r) => {
      const legValue: Record<TaxLegKey, Money> = {
        cgst: r.cgstPaise,
        sgst: r.sgstPaise,
        igst: r.igstPaise,
        cess: r.cessPaise,
      };
      const tds = [
        `<td class="doc-ctr">${esc(r.hsnSac)}</td>`,
        `<td class="doc-num">${formatAmount(r.taxablePaise)}</td>`,
        ...legs.map((leg) => `<td class="doc-num">${formatAmount(legValue[leg])}</td>`),
      ].join('');
      return `<tr>${tds}</tr>`;
    })
    .join('');

  return `
<div class="doc-summary-block">
  <div class="doc-summary-title">HSN Summary</div>
  <table class="doc-summary">
    <colgroup>${colgroupMm(widths, cols)}</colgroup>
    <thead><tr>${head}</tr></thead>
    <tbody>${rows}</tbody>
  </table>
</div>`;
}

/**
 * The foot of the document: terms, notes, declaration, bank, signature.
 *
 * EVERY VALUE HERE COMES FROM THE STORED DOCUMENT.
 * --------------------------------------------
 * `m.terms`, `m.notes` and `m.declaration` are the snapshot taken when the
 * document was created. The business profile is deliberately NOT read here.
 * An issued invoice is a historical record: if printing re-read the profile's
 * current terms, editing the profile tomorrow would silently rewrite the foot of
 * every invoice already sent to a customer - and the customer's copy would no
 * longer match the one on file. That is the whole reason those three columns
 * exist on the document.
 *
 * This renderer used to defeat that in two ways. It substituted a hardcoded
 * "We declare that this invoice shows the actual price..." sentence whenever the
 * document carried no declaration, so a business that had deliberately left it
 * blank still got a declaration printed; and `A4RenderOptions.declaration` let a
 * caller override the snapshot with any string at all, which is a channel for
 * exactly the live-value read this is meant to prevent. Both are gone.
 *
 * Each block is emitted ONLY when it has content, so no empty "Terms:" or
 * "Notes:" label can ever appear on a document with nothing to say.
 */
function renderFooter(m: NormalizedDocument, biz: BusinessIdentity): string {
  const labelled = (label: string, value?: string): string =>
    value
      ? `<div class="doc-foot-block"><span class="doc-label">${esc(label)}</span>${esc(value)}</div>`
      : '';

  const isPurchaseInvoice = m.docType === 'PURCHASE_INVOICE';
  const isPurchaseOrder = m.docType === 'PURCHASE_ORDER';
  const bankLabel = isPurchaseInvoice || isPurchaseOrder ? 'Remit to' : 'Bank Details';

  const bank =
    (biz.bankName || biz.accountNumber) &&
    m.docType !== 'DELIVERY_NOTE' &&
    m.docType !== 'PROFORMA' &&
    m.docType !== 'SALES_RETURN' &&
    m.docType !== 'SALES_ORDER'
      ? `<div class="doc-foot-block">
           <span class="doc-label">${esc(bankLabel)}</span>
           ${[
             biz.bankName ?? '',
             biz.accountNumber ? `A/c: ${biz.accountNumber}` : '',
             biz.ifsc ? `IFSC: ${biz.ifsc}` : '',
           ]
             // An explicit `filter(Boolean)` leaves the array typed
             // `(string | undefined)[]` here, so `esc` would have to accept
             // undefined. The guard states the narrowing instead.
             .filter((line): line is string => line !== '')
             .map((l) => esc(l))
             .join('<br/>')}
         </div>`
      : '';

  // Terms before notes: a reader scans for the terms first, because they are
  // the part that binds. Declaration last, immediately above the signature,
  // which is where it is signed.
  // The signature image goes between "For <company>" and "Authorised Signatory",
  // which is where the ink physically belongs. Above the company line would put
  // it in the bank-details column; below the signatory line would put it under
  // the words, which is not how anyone signs.
  const signature = biz.signatureDataUrl
    ? `<img class="doc-signature" src="${esc(biz.signatureDataUrl)}" alt="">`
    : '';

  return `
${m.docType === 'PURCHASE_ORDER' ? '' : labelled('Notes', m.notes)}
${m.docType === 'PURCHASE_ORDER' ? '' : labelled('Terms:', m.docType === 'QUOTATION' ? undefined : m.terms)}
${labelled('Declaration', m.declaration)}
${isPurchaseInvoice && (m as any).itcAvailable === true ? '<div class="doc-foot-block"><span class="doc-label">ITC:</span> Available</div>' : ''}
<table class="doc-sign">
  <colgroup>${colgroupMm({ l: 104, r: 86 }, ['l', 'r'])}</colgroup>
  <tbody><tr>
    <td>${bank}</td>
    <td class="doc-sign-right">
      <div class="doc-strong">For ${esc(biz.legalName)}</div>
      ${signature}
      <div style="margin-top:${signature ? '1mm' : '7mm'}">${m.docType === 'PURCHASE_RECEIPT' ? 'Received By' : 'Authorised Signatory'}</div>
    </td>
  </tr></tbody>
</table>
<div class="doc-computed">This is a computer-generated document.</div>`;
}

/**
 * Purchase Order header.
 *
 * Structurally different from the invoice header:
 * - A top bar carries the business mark on the left and the document title &
 *   number on the right, rather than centring the title above a two-column
 *   letterhead.
 * - Two labelled panels — "FROM" (the buying business) and "TO" (the supplier)
 *   — replace the captioned party column, giving each side equal, explicit
 *   presence.
 * - An order information strip (date, delivery-by, buyer's ref) sits below the
 *   panels as its own visual band, rather than nesting into the right column.
 * - Delivery instructions are a separate block, not inline in the header.
 *
 * This is the layout the PO wireframes §2.3 describe. It must NOT be reused for
 * any other document type — the visual hierarchy is specific to procurement.
 */
function renderPurchaseOrderHeader(
  m: NormalizedDocument,
  biz: BusinessIdentity,
  opt: A4RenderOptions,
): string {
  const title = TITLE_BY_DOC_TYPE[m.docType] ?? m.docType;
  const numberLabel = NUMBER_LABEL_BY_DOC_TYPE[m.docType] ?? 'Order No.';

  const contact = (phone?: string, email?: string): string => {
    const parts = [phone ? `Ph: ${phone}` : '', email ?? ''].filter(Boolean);
    return parts.length ? `<div class="doc-biz-line">${esc(parts.join('  ·  '))}</div>` : '';
  };

  const logo = biz.logoDataUrl
    ? `<img class="doc-logo" src="${esc(biz.logoDataUrl)}" alt="${esc(biz.legalName)}">`
    : '';

  const bizBlock = [
    `<div class="doc-biz-name">${esc(biz.legalName)}</div>`,
    biz.tradeName && biz.tradeName !== biz.legalName
      ? `<div class="doc-biz-line">${esc(biz.tradeName)}</div>`
      : '',
    ...biz.addressLines.map((a) => `<div class="doc-biz-line">${esc(a)}</div>`),
    biz.gstin ? `<div class="doc-gstin">GSTIN: ${esc(biz.gstin)}</div>` : '',
    contact(biz.phone, biz.email),
    biz.website ? `<div class="doc-biz-line">${esc(biz.website)}</div>` : '',
  ]
    .filter(Boolean)
    .join('');

  const p = m.party;
  const supplierBlock = [
    `<div class="doc-biz-name">${esc(p.name)}</div>`,
    ...p.addressLines.map((a) => `<div class="doc-biz-line">${esc(a)}</div>`),
    p.gstin ? `<div class="doc-gstin">GSTIN: ${esc(p.gstin)}</div>` : '',
    contact(p.phone, p.email),
  ]
    .filter(Boolean)
    .join('');

  const draftBanner =
    m.status === 'DRAFT'
      ? `<div class="doc-draft-banner">Draft — not yet sent to the supplier</div>`
      : '';

  const infoItems: Array<readonly [string, string] | null> = [
    ['Order Date', m.docDate],
    m.expectedDate ? ['Delivery By', m.expectedDate] : null,
    opt.referenceNumber ? ["Buyer's Ref", opt.referenceNumber] : null,
    m.dueDate ? ['Due Date', m.dueDate] : null,
    ['Place of Supply', `${stateNameFor(m.placeOfSupplyCode)} (${m.placeOfSupplyCode})`],
  ];
  const infoStrip = infoItems
    .filter((r): r is readonly [string, string] => r !== null)
    .map(
      ([k, v]) =>
        `<div class="doc-po-info-item"><span class="doc-po-info-label">${esc(k)}</span><span class="doc-po-info-value">${esc(v)}</span></div>`,
    )
    .join('');

  const deliveryBlock = m.notes
    ? `<div class="doc-po-delivery">
         <span class="doc-label">Delivery Instructions:</span> ${esc(m.notes)}
       </div>`
    : '';

  return `
<div class="doc-head doc-po-head">
  <div class="doc-po-bar">
    <div class="doc-po-bar-left">
      ${logo}
      <div class="doc-po-business">${bizBlock}</div>
    </div>
    <div class="doc-po-bar-right">
      <div class="doc-title">${esc(title)}</div>
      <div class="doc-po-docmeta">
        <div class="doc-po-number">${esc(numberLabel)} ${esc(m.docNumber)}</div>
        <div class="doc-po-date">Date: ${esc(m.docDate)}</div>
      </div>
      ${draftBanner}
    </div>
  </div>
  <table class="doc-po-panels">
    <colgroup>${colgroupMm(LETTERHEAD_COLUMNS_MM, ['seller', 'buyer'])}</colgroup>
    <tbody>
      <tr>
        <td class="doc-po-panel doc-po-from">
          <div class="doc-caption">FROM</div>
          ${bizBlock}
        </td>
        <td class="doc-po-panel doc-po-to">
          <div class="doc-caption">TO</div>
          ${supplierBlock}
        </td>
      </tr>
    </tbody>
  </table>
  ${infoStrip ? `<div class="doc-po-info-strip">${infoStrip}</div>` : ''}
  ${deliveryBlock}
</div>`;
}

/**
 * Numbered Terms & Conditions for Purchase Orders.
 *
 * Purchase Orders carry their terms as a numbered list — a supplier reads
 * "1." not "Terms:" when planning which clause binds. This is rendered as a
 * dedicated block between the totals and the footer, rather than as a single
 * labelled string in the footer, per the PO wireframes §2.3.
 */
function renderTermsAndConditions(m: NormalizedDocument): string {
  if (m.docType !== 'PURCHASE_ORDER' || !m.terms) return '';
  const lines = m.terms.split('\n').filter((line) => line.trim() !== '');
  const items = lines
    .map((line, i) => `<div class="doc-tnc-item">${i + 1}. ${esc(line.trim())}</div>`)
    .join('');
  return `
<div class="doc-tnc-block">
  <div class="doc-tnc-title">Terms &amp; Conditions</div>
  ${items}
</div>`;
}

/**
 * Purchase Order layout composition.
 *
 * PO is an order, not a tax document. Its structure follows the wireframes §2.3:
 * header → items → totals → numbered terms → footer. The tax summary and HSN
 * summary tables are omitted — an order is a pre-tax confirmation. Delivery
 * instructions live in the header (from `m.notes`), and the numbered Terms &
 * Conditions occupy their own section between totals and footer.
 */
function composePurchaseOrder(
  m: NormalizedDocument,
  biz: BusinessIdentity,
  opt: A4RenderOptions,
): string {
  const header = renderPurchaseOrderHeader(m, biz, opt);
  const items = renderItems(m);
  const totals = renderTotals(m);
  const termsAndConditions = renderTermsAndConditions(m);
  const footer = renderFooter(m, biz);

  return `${header}
${items}
${totals}
${termsAndConditions}
${footer}`;
}

/**
 * Render a full, standalone A4 HTML document.
 *
 * @param assetsBaseUrl directory the two TTFs are served from, WITH a trailing
 *        slash, e.g. `new URL('assets/', location.href).href`. The previous
 *        comment here claimed `''` produced a "fully inlined document" — it
 *        never did. An empty prefix produces a DOCUMENT-RELATIVE url() that
 *        silently fails to resolve unless the fonts happen to sit beside the
 *        document, and the page then prints in a substitute font with different
 *        glyph metrics. The font is ~1 MB per face, so it is not inlined; the
 *        guarantee comes from `assertDocFontsLoaded(document.fonts)`, which every
 *        print and PDF path must call after `document.fonts.ready`.
 */
/**
 * The document's body, and nothing else.
 *
 * Exported so the on-screen preview can render the SAME markup rather than a
 * second implementation of it. Two copies of a document template drift, and a
 * preview that has quietly diverged from the print is worse than no preview:
 * it shows the user something that is not what will be printed. `renderA4()`
 * and `renderPreview()` both call this, which is what makes their output
 * identical by construction rather than by careful copying.
 */
/**
 * Document-type-specific layout composition.
 *
 * Every document type shares the same rendering primitives (header, items,
 * totals, tax summary, HSN summary, acknowledgement, footer) but composes
 * them in a different order and with different emphasis, according to its
 * business purpose.
 *
 * The shared `renderA4Body()` contract is preserved — both preview and print
 * paths call it, and it returns identical markup for the same input.
 */
function composeLayout(m: NormalizedDocument, biz: BusinessIdentity, opt: A4RenderOptions): string {
  const header = renderHeader(m, biz, opt);
  const items = renderItems(m);
  const totals = renderTotals(m);
  const taxSummary = renderTaxSummary(m);
  const hsnSummary = renderHsnSummary(m);
  const acknowledgement = renderAcknowledgement(m);
  const footer = renderFooter(m, biz);

  // ── Delivery Note: goods movement. Items → minimal totals → receiver
  //     acknowledgement → footer (no bank details). ──
  if (m.docType === 'DELIVERY_NOTE') {
    return `${header}
${items}
${totals}
${taxSummary}
${hsnSummary}
${acknowledgement}
${footer}`;
  }

  // ── Purchase Order: order confirmation. Items → totals → numbered terms →
  //     footer (no tax/HSN summary, bank details labelled "Remit to"). ──
  if (m.docType === 'PURCHASE_ORDER') {
    return composePurchaseOrder(m, biz, opt);
  }

  // ── Standard composition for all other types: header → items → totals →
  //     tax → HSN → footer. Distinction is achieved through per-type CSS
  //     classes on .doc-page (doc-mode-*) and through per-type rendering
  //     choices already made inside each primitive. ──
  return `${header}
${items}
${totals}
${taxSummary}
${hsnSummary}
${acknowledgement}
${footer}`;
}

export function renderA4Body(
  m: NormalizedDocument,
  biz: BusinessIdentity,
  opt: A4RenderOptions = {},
): string {
  return composeLayout(m, biz, opt);
}

/**
 * Receiver acknowledgement section for Delivery Note.
 */
function renderAcknowledgement(m: NormalizedDocument): string {
  if (m.docType !== 'DELIVERY_NOTE') return '';
  return `
<div class="doc-ack">
  <div class="doc-ack-check">
    <span class="doc-ack-checkbox">&#9744;</span>
    <span class="doc-ack-checklabel">Goods received in good condition</span>
  </div>
  <table class="doc-ack-table">
    <tbody>
      <tr>
        <td style="width:50%">
          <div class="doc-ack-label">Received by</div>
          <div class="doc-ack-line"></div>
          <div class="doc-ack-caps">RECEIVER NAME / DESIGNATION</div>
        </td>
        <td style="width:50%">
          <div class="doc-ack-label">Date of Delivery</div>
          <div class="doc-ack-line"></div>
          <div class="doc-ack-caps">DD-MM-YYYY</div>
        </td>
      </tr>
    </tbody>
  </table>
  <div class="doc-ack-total">
    <span class="doc-ack-total-label">Total Qty Delivered:</span>
    <span class="doc-ack-total-value">${formatQuantity(m.totalQtyMilli)}</span>
  </div>
</div>`;
}

export function renderA4(
  m: NormalizedDocument,
  biz: BusinessIdentity,
  opt: A4RenderOptions = {},
  assetsBaseUrl = '',
): string {
  const title = TITLE_BY_DOC_TYPE[m.docType] ?? m.docType;
  const mode = modeClass(m.docType);
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${esc(title)} ${esc(m.docNumber)}</title>
<style>${a4Stylesheet(assetsBaseUrl)}</style>
</head>
<body>
<div class="doc-page ${mode}">
  ${renderA4Body(m, biz, opt)}
</div>
</body>
</html>`;
}
